import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  Play, 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  Building, 
  UserCheck, 
  Shield,
  Database,
  Mail,
  CreditCard,
  Search,
  FileText,
  Users
} from 'lucide-react';

interface TestStep {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'running' | 'passed' | 'failed';
  error?: string;
  duration?: number;
}

interface WorkflowTest {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  steps: TestStep[];
  status: 'not-started' | 'running' | 'completed' | 'failed';
  progress: number;
}

const EndToEndWorkflowTesting = () => {
  const [workflows, setWorkflows] = useState<WorkflowTest[]>([
    {
      id: 'al-provider-journey',
      name: 'Assisted Living Provider Journey',
      description: 'Test the full assisted living provider workflow from signup to managing referrals',
      icon: Building,
      status: 'not-started',
      progress: 0,
      steps: [
        {
          id: 'al-provider-signup',
          name: 'AL Provider Account Creation',
          description: 'Create a new assisted living provider account with NPI',
          status: 'pending'
        },
        {
          id: 'al-provider-subscription',
          name: 'Subscription Setup',
          description: 'Set up subscription and payment method',
          status: 'pending'
        },
        {
          id: 'facility-verification',
          name: 'Facility Verification',
          description: 'Verify facility with MN Scorecard data',
          status: 'pending'
        },
        {
          id: 'listing-creation',
          name: 'Bed Listing Creation',
          description: 'Create bed availability listing',
          status: 'pending'
        },
        {
          id: 'housing-referral-management',
          name: 'Housing Referral Management',
          description: 'Receive and respond to housing placement referrals',
          status: 'pending'
        },
        {
          id: 'analytics-access',
          name: 'Analytics Dashboard',
          description: 'Access provider analytics and metrics',
          status: 'pending'
        }
      ]
    },
    {
      id: 'hss-provider-journey',
      name: 'HSS Provider Journey',
      description: 'Test the full HSS service provider workflow from signup to managing service referrals',
      icon: Users,
      status: 'not-started',
      progress: 0,
      steps: [
        {
          id: 'hss-signup',
          name: 'HSS Provider Account Creation',
          description: 'Create HSS provider account with agency details',
          status: 'pending'
        },
        {
          id: 'hss-subscription',
          name: 'HSS Subscription Setup',
          description: 'Set up HSS provider subscription',
          status: 'pending'
        },
        {
          id: 'county-configuration',
          name: 'County Coverage Setup',
          description: 'Configure counties served and populations',
          status: 'pending'
        },
        {
          id: 'service-capacity-management',
          name: 'Service Capacity Management',
          description: 'Manage accepting clients status',
          status: 'pending'
        },
        {
          id: 'hss-referral-management',
          name: 'HSS Service Referral Management',
          description: 'Receive and respond to HSS service referrals',
          status: 'pending'
        },
        {
          id: 'hss-analytics-access',
          name: 'HSS Analytics Dashboard',
          description: 'Access HSS provider metrics and outcomes',
          status: 'pending'
        }
      ]
    },
    {
      id: 'case-manager-journey',
      name: 'Case Manager Complete Journey',
      description: 'Test case manager workflow for both housing placement and HSS service referrals',
      icon: UserCheck,
      status: 'not-started',
      progress: 0,
      steps: [
        {
          id: 'cm-signup',
          name: 'Case Manager Signup',
          description: 'Create case manager account',
          status: 'pending'
        },
        {
          id: 'referral-type-selection',
          name: 'Referral Type Selection',
          description: 'Choose between housing placement and HSS service referrals',
          status: 'pending'
        },
        {
          id: 'housing-facility-search',
          name: 'Housing Facility Search',
          description: 'Search for available beds with filters',
          status: 'pending'
        },
        {
          id: 'housing-referral-submission',
          name: 'Housing Referral Submission',
          description: 'Submit housing placement referral with HIPAA compliance',
          status: 'pending'
        },
        {
          id: 'hss-service-referral',
          name: 'HSS Service Referral',
          description: 'Submit HSS service referral with support needs',
          status: 'pending'
        },
        {
          id: 'referral-tracking',
          name: 'Referral Tracking',
          description: 'Track both housing and HSS referral statuses',
          status: 'pending'
        },
        {
          id: 'placement-completion',
          name: 'Service Completion',
          description: 'Complete client placement or service coordination',
          status: 'pending'
        }
      ]
    },
    {
      id: 'admin-journey',
      name: 'Admin Management Journey',
      description: 'Test administrative functions and system oversight',
      icon: Shield,
      status: 'not-started',
      progress: 0,
      steps: [
        {
          id: 'admin-login',
          name: 'Admin Authentication',
          description: 'Admin login and role verification',
          status: 'pending'
        },
        {
          id: 'user-management',
          name: 'User Management',
          description: 'View and manage user accounts',
          status: 'pending'
        },
        {
          id: 'listing-oversight',
          name: 'Listing Oversight',
          description: 'Monitor and manage all listings',
          status: 'pending'
        },
        {
          id: 'analytics-review',
          name: 'System Analytics',
          description: 'Review platform metrics and performance',
          status: 'pending'
        },
        {
          id: 'support-management',
          name: 'Support Ticket Management',
          description: 'Handle user support requests',
          status: 'pending'
        },
        {
          id: 'alert-handling',
          name: 'System Alert Handling',
          description: 'Manage system alerts and notifications',
          status: 'pending'
        }
      ]
    },
    {
      id: 'system-integration',
      name: 'System Integration Tests',
      description: 'Test core system integrations and services including HSS workflows',
      icon: Database,
      status: 'not-started',
      progress: 0,
      steps: [
        {
          id: 'email-notifications',
          name: 'Email Notification System',
          description: 'Test all email notification types (housing & HSS)',
          status: 'pending'
        },
        {
          id: 'payment-processing',
          name: 'Payment Processing',
          description: 'Test Stripe subscription flows for both provider types',
          status: 'pending'
        },
        {
          id: 'scorecard-integration',
          name: 'Scorecard Data Integration',
          description: 'Test MN Scorecard data retrieval for AL providers',
          status: 'pending'
        },
        {
          id: 'file-uploads',
          name: 'File Upload System',
          description: 'Test referral document uploads (housing & HSS)',
          status: 'pending'
        },
        {
          id: 'county-matching',
          name: 'County-based Referral Matching',
          description: 'Test HSS provider county-based referral routing',
          status: 'pending'
        },
        {
          id: 'security-compliance',
          name: 'Security & HIPAA Compliance',
          description: 'Verify data security and access controls',
          status: 'pending'
        }
      ]
    }
  ]);

  const [activeTest, setActiveTest] = useState<string | null>(null);
  const [overallProgress, setOverallProgress] = useState(0);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'running':
        return <Clock className="w-4 h-4 text-blue-600 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'passed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'running':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const runWorkflowTest = async (workflowId: string) => {
    setActiveTest(workflowId);
    
    // Update workflow status to running
    setWorkflows(prev => prev.map(w => 
      w.id === workflowId 
        ? { ...w, status: 'running', progress: 0 }
        : w
    ));

    const workflow = workflows.find(w => w.id === workflowId);
    if (!workflow) return;

    // Simulate running each step
    for (let i = 0; i < workflow.steps.length; i++) {
      const step = workflow.steps[i];
      
      // Update step to running
      setWorkflows(prev => prev.map(w => 
        w.id === workflowId 
          ? {
              ...w, 
              steps: w.steps.map(s => 
                s.id === step.id 
                  ? { ...s, status: 'running' }
                  : s
              ),
              progress: Math.round(((i) / w.steps.length) * 100)
            }
          : w
      ));

      // Simulate test execution time
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

      // Simulate test result (90% pass rate for demo)
      const passed = Math.random() > 0.1;
      const duration = Math.round(1000 + Math.random() * 2000);

      // Update step result
      setWorkflows(prev => prev.map(w => 
        w.id === workflowId 
          ? {
              ...w, 
              steps: w.steps.map(s => 
                s.id === step.id 
                  ? { 
                      ...s, 
                      status: passed ? 'passed' : 'failed',
                      duration,
                      error: passed ? undefined : `Simulated error in ${step.name}`
                    }
                  : s
              ),
              progress: Math.round(((i + 1) / w.steps.length) * 100)
            }
          : w
      ));
    }

    // Update workflow final status
    const finalWorkflow = workflows.find(w => w.id === workflowId);
    const hasFailures = finalWorkflow?.steps.some(s => s.status === 'failed');
    
    setWorkflows(prev => prev.map(w => 
      w.id === workflowId 
        ? { 
            ...w, 
            status: hasFailures ? 'failed' : 'completed',
            progress: 100
          }
        : w
    ));

    setActiveTest(null);
    
    // Update overall progress
    const totalSteps = workflows.reduce((acc, w) => acc + w.steps.length, 0);
    const completedSteps = workflows.reduce((acc, w) => 
      acc + w.steps.filter(s => s.status === 'passed' || s.status === 'failed').length, 0
    );
    setOverallProgress(Math.round((completedSteps / totalSteps) * 100));
  };

  const runAllTests = async () => {
    for (const workflow of workflows) {
      await runWorkflowTest(workflow.id);
    }
  };

  const resetAllTests = () => {
    setWorkflows(prev => prev.map(w => ({
      ...w,
      status: 'not-started',
      progress: 0,
      steps: w.steps.map(s => ({
        ...s,
        status: 'pending',
        error: undefined,
        duration: undefined
      }))
    })));
    setOverallProgress(0);
    setActiveTest(null);
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Play className="w-6 h-6" />
            <span>End-to-End Workflow Testing</span>
          </CardTitle>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Comprehensive testing of all CareKonnect user journeys and system integrations, including 
              housing placement and HSS service provider workflows.
            </p>
            
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-2">
                  <span>Overall Progress</span>
                  <span>{overallProgress}%</span>
                </div>
                <Progress value={overallProgress} className="h-2" />
              </div>
              <div className="flex space-x-2">
                <Button 
                  onClick={runAllTests} 
                  disabled={activeTest !== null}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Run All Tests
                </Button>
                <Button 
                  onClick={resetAllTests} 
                  variant="outline"
                  disabled={activeTest !== null}
                >
                  Reset All
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Test Workflows */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="al-provider-journey">AL Provider</TabsTrigger>
          <TabsTrigger value="hss-provider-journey">HSS Provider</TabsTrigger>
          <TabsTrigger value="case-manager-journey">Case Manager</TabsTrigger>
          <TabsTrigger value="admin-journey">Admin</TabsTrigger>
          <TabsTrigger value="system-integration">System</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {workflows.map((workflow) => {
              const Icon = workflow.icon;
              const passedSteps = workflow.steps.filter(s => s.status === 'passed').length;
              const failedSteps = workflow.steps.filter(s => s.status === 'failed').length;
              const totalSteps = workflow.steps.length;

              return (
                <Card key={workflow.id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Icon className="w-6 h-6 text-primary" />
                        <div>
                          <CardTitle className="text-lg">{workflow.name}</CardTitle>
                          <p className="text-sm text-muted-foreground">{workflow.description}</p>
                        </div>
                      </div>
                      <Badge className={getStatusColor(workflow.status)}>
                        {workflow.status === 'running' ? 'Running' : 
                         workflow.status === 'completed' ? 'Complete' :
                         workflow.status === 'failed' ? 'Failed' : 'Ready'}
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{workflow.progress}%</span>
                    </div>
                    <Progress value={workflow.progress} className="h-2" />
                    
                    <div className="flex justify-between items-center">
                      <div className="flex space-x-4 text-sm">
                        <span className="text-green-600">✓ {passedSteps} passed</span>
                        <span className="text-red-600">✗ {failedSteps} failed</span>
                        <span className="text-gray-600">{totalSteps - passedSteps - failedSteps} pending</span>
                      </div>
                      
                      <Button 
                        size="sm" 
                        onClick={() => runWorkflowTest(workflow.id)}
                        disabled={activeTest !== null}
                        variant={workflow.status === 'completed' ? 'outline' : 'default'}
                      >
                        {activeTest === workflow.id ? (
                          <>
                            <Clock className="w-4 h-4 mr-2 animate-spin" />
                            Running
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            {workflow.status === 'completed' ? 'Rerun' : 'Start'}
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {workflows.map((workflow) => (
          <TabsContent key={workflow.id} value={workflow.id} className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <workflow.icon className="w-6 h-6 text-primary" />
                    <div>
                      <CardTitle>{workflow.name}</CardTitle>
                      <p className="text-muted-foreground">{workflow.description}</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => runWorkflowTest(workflow.id)}
                    disabled={activeTest !== null}
                  >
                    {activeTest === workflow.id ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Running
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Run Test
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {workflow.steps.map((step, index) => (
                    <div key={step.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-sm font-medium">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{step.name}</div>
                          <div className="text-sm text-muted-foreground">{step.description}</div>
                          {step.error && (
                            <Alert className="mt-2 py-2">
                              <AlertDescription className="text-sm text-red-600">
                                {step.error}
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {step.duration && (
                          <span className="text-sm text-muted-foreground">
                            {step.duration}ms
                          </span>
                        )}
                        {getStatusIcon(step.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default EndToEndWorkflowTesting;